import {
  ReactNode,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useState,
} from "react"

import { useAuth } from "./auth-context"
import { useNavigate, useLocation } from "react-router-dom"
import { useToast } from "@/hooks/use-toast"
import LoadingSkeleton from "@/components/shared/LoadingSkeleton"
import ApiErroPage from "@/components/shared/ApiErrorPage"
import { FRONTEND_ROUTES } from "@/constants"
import { PermissionContextData } from "@/definitions/types/permissions/permissions"
import { PermissionsRoles } from "@/definitions/enums/permissions"
import NoContentAvailable from "@/components/shared/NoContentAvailable"
import { STRINGS } from "@/constants/strings"
import { useGetPermissions } from "@/hooks/permissions"
import { useAppConfig } from "./config-provider"

const PermissionContext = createContext(
  {} as PermissionContextData
)

interface PermissionProviderProps {
  children: ReactNode
}

export function PermissionProvider({
  children,
}: PermissionProviderProps) {
  const { accessToken } = useAuth()
  const config = useAppConfig()
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname
  const { toast } = useToast()
  const [isCheckingPermission, setIsCheckingPermission] =
    useState(true)
  const {
    data: rolesAndBrokersData,
    isLoading,
    error,
  } = useGetPermissions(accessToken)

  const handleUnauthorizedAccess = useCallback(() => {
    toast({
      title:
        "Você não tem permissão para acessar esta página",
      description:
        "Por favor, contate o administrador do sistema para mais informações.",
      variant: "destructive",
    })
    navigate(FRONTEND_ROUTES.MAIN.route, { replace: true })
  }, [navigate, toast])

  const getCurrentRoute = useCallback(() => {
    const currentPath = pathname.split("?")[0]

    const route = Object.entries(FRONTEND_ROUTES).find(
      ([, routeConfig]) => {
        const routePath = routeConfig.route
          .split("?")[0]
          .trim()

        return currentPath === routePath
      }
    )

    return route?.[1]
  }, [pathname])

  const checkAccess = useCallback(() => {
    if (pathname === FRONTEND_ROUTES.LOGIN.route) {
      return true
    }
    if (pathname === FRONTEND_ROUTES.MAIN.route) {
      return true
    }

    if (!rolesAndBrokersData) {
      return false
    }

    const route = getCurrentRoute()
    if (!route) {
      return true
    }

    if (rolesAndBrokersData.superAdmin) {
      return true
    }

    if (route.requireSuperAdmin) {
      return false
    }

    if (route.permissionRoles.length === 0) {
      return true
    }

    return route.permissionRoles.every((permission) =>
      rolesAndBrokersData.roles.includes(permission)
    )
  }, [rolesAndBrokersData, getCurrentRoute, pathname])

  useEffect(() => {
    if (isLoading || !rolesAndBrokersData) return

    const accessResult = checkAccess()

    if (!accessResult) {
      handleUnauthorizedAccess()
    }

    setIsCheckingPermission(false)
  }, [
    pathname,
    rolesAndBrokersData,
    isLoading,
    checkAccess,
    handleUnauthorizedAccess,
  ])

  const hasPermission = useCallback(
    (permissions: PermissionsRoles[]) => {
      if (!rolesAndBrokersData) return false
      if (rolesAndBrokersData.superAdmin) return true
      return permissions.every((permission) =>
        rolesAndBrokersData.roles.includes(permission)
      )
    },
    [rolesAndBrokersData]
  )

  const contextValue = useMemo(
    () => ({
      rolesAndBrokersData,
      isLoading: isLoading || isCheckingPermission,
      hasPermission,
      isSuperAdmin:
        rolesAndBrokersData?.superAdmin ?? false,
    }),
    [
      rolesAndBrokersData,
      isLoading,
      isCheckingPermission,
      hasPermission,
    ]
  )

  if (error && !rolesAndBrokersData)
    return <ApiErroPage error={error} />

  // Só mostra loading se não tiver dados do cache
  if (isLoading && !rolesAndBrokersData)
    return <LoadingSkeleton />

  if (rolesAndBrokersData?.roles?.length === 0)
    return (
      <NoContentAvailable
        title={STRINGS.NO_PERMISSIONS_TITLE}
        message={
          config.visualPreferences.layout.texts
            .userAccessMessage ||
          STRINGS.NO_PERMISSIONS_MESSAGE
        }
      />
    )

  if (rolesAndBrokersData?.brokers?.length === 0)
    return (
      <NoContentAvailable
        title={STRINGS.NO_BROKER_TITLE}
        message={STRINGS.NO_BROKER_MESSAGE}
      />
    )

  if (!checkAccess()) return <LoadingSkeleton />

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  )
}

export const usePermissions = () => {
  const context = useContext(PermissionContext)
  if (!context) {
    throw new Error(
      "usePermissions must be used within a PermissionProvider"
    )
  }
  return context
}
